/**
 * DonutChart - Distribution with center stat
 *
 * Usage:
 *   <DonutChart
 *     title="Time Breakdown"
 *     data={{
 *       segments: [
 *         { label: 'Work', value: 8 },
 *         { label: 'Sleep', value: 7 },
 *         { label: 'Exercise', value: 1 },
 *         { label: 'Other', value: 8 },
 *       ],
 *       centerValue: '24',
 *       centerLabel: 'hours'
 *     }}
 *   />
 */

import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import type { BlockProps } from './types';
import { getSeriesColor } from './colors';
import './blocks.css';

export interface DonutChartData {
  segments: Array<{
    label: string;
    value: number;
    color?: string;
  }>;
  /** Value to display in center */
  centerValue?: string | number;
  /** Label below center value */
  centerLabel?: string;
}

export interface DonutChartProps extends BlockProps<DonutChartData> {
  /** Chart size in pixels */
  size?: number;
  /** Donut thickness */
  thickness?: number;
}

export function DonutChart({
  data,
  title,
  config,
  size = 200,
  thickness = 30,
  className,
}: DonutChartProps) {
  const { segments, centerValue, centerLabel } = data;

  if (!segments || segments.length === 0) {
    return (
      <div className={`block-chart ${className || ''}`}>
        {title && <div className="block-chart-title">{title}</div>}
        <div style={{ padding: '40px 20px', textAlign: 'center', color: 'var(--color-text-faint)' }}>
          No data available
        </div>
      </div>
    );
  }

  const total = segments.reduce((sum, s) => sum + s.value, 0);
  const outerRadius = size / 2 - 10;
  const innerRadius = outerRadius - thickness;

  const CustomTooltip = ({ active, payload }: any) => {
    if (!active || !payload?.length) return null;

    const segment = payload[0];
    // Guard against division by zero when all segments are 0
    const percentage = total === 0 ? '0.0' : ((segment.value / total) * 100).toFixed(1);

    return (
      <div style={{
        backgroundColor: 'var(--color-surface)',
        border: '1px solid var(--color-border)',
        borderRadius: '6px',
        padding: '8px 12px',
        boxShadow: 'var(--popover-shadow, 0 2px 4px rgba(0,0,0,0.1))',
      }}>
        <p style={{
          color: segment.payload.fill,
          fontWeight: 600,
          margin: 0,
          fontSize: '14px',
        }}>
          {segment.name}: {segment.value.toLocaleString()} ({percentage}%)
        </p>
      </div>
    );
  };

  return (
    <div className={`block-chart ${className || ''}`}>
      {title && <div className="block-chart-title">{title}</div>}

      <div className="block-chart-container" style={{ position: 'relative' }}>
        <ResponsiveContainer width="100%" height={size}>
          <PieChart>
            <Pie
              data={segments}
              dataKey="value"
              nameKey="label"
              cx="50%"
              cy="50%"
              innerRadius={innerRadius}
              outerRadius={outerRadius}
              paddingAngle={2}
              startAngle={90}
              endAngle={-270}
            >
              {segments.map((segment, index) => (
                <Cell
                  key={index}
                  fill={segment.color || config?.colors?.[index] || getSeriesColor(index)}
                />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
          </PieChart>
        </ResponsiveContainer>

        {/* Center content */}
        {(centerValue !== undefined || centerLabel) && (
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center',
            pointerEvents: 'none',
          }}>
            {centerValue !== undefined && (
              <div style={{
                fontSize: '28px',
                fontWeight: 700,
                color: 'var(--color-text)',
                lineHeight: 1.2,
              }}>
                {centerValue}
              </div>
            )}
            {centerLabel && (
              <div style={{
                fontSize: '12px',
                color: 'var(--color-text-muted)',
                marginTop: '2px',
              }}>
                {centerLabel}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Legend */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '12px',
        justifyContent: 'center',
        marginTop: '12px',
      }}>
        {segments.map((segment, index) => (
          <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <div style={{
              width: 10,
              height: 10,
              borderRadius: '2px',
              backgroundColor: segment.color || config?.colors?.[index] || getSeriesColor(index),
            }} />
            <span style={{ fontSize: '12px', color: 'var(--color-text-subtle)' }}>
              {segment.label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default DonutChart;
